import { colors, screen } from 'components/styles/variables';
import eventLogger from 'lib/EventLogger';
import ReadMoreClickEvent from 'lib/Tracking/Srp/ReadMoreClickEvent';
import { ArticlePreview } from 'lib/AutolistAPI';
import { format } from 'date-fns';

export interface NewsProps {
  make?: string;
  model?: string;
  articles: ArticlePreview[];
}

export default function News(props: NewsProps): JSX.Element {
  const { make, model } = props;

  const onReadMoreClick = (): void => {
    eventLogger.log(ReadMoreClickEvent());
  };

  return (
    <div className="news-container">
      <h2 className="news-title">
        {make && model && (
          <>
            {make} {model}&nbsp;
          </>
        )}
        News
      </h2>
      <ul className="new-list">
        {props.articles.slice(0, 3).map((article, index) => (
          <li key={article.title} className="seo-srp-news-list-item">
            <article
              className={`article-container ${
                index === props.articles.length - 1 ? 'no-border' : ''
              }`}
            >
              <figure className="thumbnail-container">
                <img className="article-thumbnail" src={article.thumbImage} loading="lazy" />
              </figure>
              <section className="article-summary">
                <div>
                  <div className="byline">
                    <span className="author">
                      <address>{article.author.name}</address>
                    </span>
                    <span className="ellipse" />
                    <span className="published">
                      <time>{format(new Date(article.publicationDate), 'MM/dd/yyyy')}</time>
                    </span>
                  </div>
                  <div className="title">
                    <h3 className="news-article-title">{article.title}</h3>
                  </div>
                  <div className="summary">
                    <p>{article.contentSummary}</p>
                  </div>
                </div>

                <div className="cta-button" onClick={onReadMoreClick}>
                  <a
                    className="cta-button-link"
                    href={`${article.parentPage.slug}/${article.slug}`}
                    target="_blank"
                  >
                    {' '}
                    Read More
                  </a>
                </div>
              </section>
            </article>
          </li>
        ))}
      </ul>
      <style jsx>
        {`
          address {
            font-style: unset;
            display: initial;
          }
          .news-article-title {
            display: initial;
            font-size: inherit;
          }
          .cta-button-link {
            color: inherit;
            font-weight: inherit;
            font-size: inherit;
          }
          .cta-button-link:hover {
            color: red;
          }
          p,
          .seo-srp-news-list-item {
            display: initial;
          }
          .new-list {
            display: block;
            padding-bottom: 32px;
            padding-inline-start: unset;
            margin-block-start: unset;
            margin-block-end: unset;
          }
          .news-title {
            font-weight: 700;
            color: ${colors.BLACK};
            line-height: 26px;
            letter-spacing: 0.2px;
            text-transform: uppercase;
            margin-top: 24px;
            font-size: 18px;
          }
          .news-container {
            width: 100%;
            margin-top: 52px;
            border-top: 1px solid ${colors.G2};
            padding: 0 16px;
          }
          .ellipse {
            width: 4px;
            height: 4px;
            background: ${colors.G3};
            border-radius: 50%;
            margin-top: 11px;
            text-align: center;
          }
          .link-bank-container {
            padding: 24px 0px;
            border-bottom: 1px solid ${colors.G2};
          }
          .byline {
            display: flex;
            gap: 4px;
            font-size: 12px;
            margin-bottom: 2px;
            font-weight: 600;
            line-height: 24px;
            letter-spacing: 0.2px;
            color: ${colors.G3};
          }
          .cta-button {
            display: none;
          }
          .summary {
            display: none;
          }
          .title {
            font-size: 18px;
            line-height: 22px;
            margin-bottom: 0;
            font-weight: 700;
            letter-spacing: 0.2px;
          }
          .article-thumbnail {
            width: 96px;
            height: 50px;
            border-radius: 8px;
          }
          .article-container {
            gap: 16px;
            display: flex;
            border-bottom: 1px solid ${colors.G2};
            padding: 16px 0;
          }
          .no-border {
            border-bottom: none;
            padding-bottom: 0;
          }
          @media (min-width: 320px) {
            .article-summary {
              max-width: 296px;
              flex-basis: 80%;
            }
            .article-thumbnail {
              max-width: 100%;
              height: auto;
            }
            .byline {
              font-size: 16px;
              margin-bottom: 4px;
            }
          }
          @media (min-width: ${screen.xsMin}) {
            .article-thumbnail {
              width: auto;
              max-width: 300px;
              max-height: 168px;
            }
            .title {
              font-size: 22px;
              line-height: 32px;
            }
          }
          @media (min-width: 850px) {
            .article-summary {
              max-width: 100%;
            }
            .article-container {
              padding: 32px 0;
              gap: unset;
            }
            .no-border {
              padding-bottom: 0;
            }
            .title {
              margin-bottom: 12px;
              max-width: 470px;
            }
            .summary {
              font-weight: 400;
              font-size: 16px;
              line-height: 24px;
              letter-spacing: 0.2px;
              margin-bottom: 23px;
              max-width: 470px;
              // allows ellipsis overflow on second line. see: https://stackoverflow.com/questions/5269713/css-ellipsis-on-second-line
              display: -webkit-box;
              -webkit-line-clamp: 2;
              -webkit-box-orient: vertical;
              overflow: hidden;
              text-overflow: ellipsis;
            }
            .news-title {
              font-size: 20px;
            }
            .thumbnail-container {
              margin-right: 24px;
            }
            .article-thumbnail {
              width: 300px;
              height: 168px;
            }
            .article-summary {
              justify-content: inherit;
            }
            .cta-button {
              font-weight: 700;
              font-size: 16px;
              line-height: 22px;
              letter-spacing: 0.2px;
              text-transform: uppercase;
              max-width: 87px;
              border-bottom: 2px solid ${colors.ALR1};
              display: initial;
            }
          }
          @media (min-width: ${screen.lgMin}) {
            .news-container {
              padding: 0;
            }
            .article-container {
              gap: unset;
            }
            .thumbnail-container {
              margin-right: 32px;
            }
          }
        `}
      </style>
    </div>
  );
}
